// Cross-tabulation of two variables with counts or percentages and a
// chi-square test of independence.
import { ArrowLeftRight, Table2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, ApiError, type ResponseFilter } from '../../api/client';
import type { AnalysisVariable, Crosstab } from '../../api/types';
import { formatNumber } from '../../format';
import { useI18n } from '../../i18n';
import { Empty } from '../ui';

type Mode = 'count' | 'row' | 'col' | 'total';

export function CrosstabPanel({ surveyId, filter, variables }: { surveyId: string; filter: ResponseFilter; variables: AnalysisVariable[] }) {
  const { t } = useI18n();
  const usable = variables.filter((v) => v.categories.length > 0);
  const [row, setRow] = useState(usable[0]?.key ?? '');
  const [col, setCol] = useState(usable[1]?.key ?? usable[0]?.key ?? '');
  const [mode, setMode] = useState<Mode>('count');
  const [data, setData] = useState<Crosstab | null>(null);
  const [error, setError] = useState('');
  const filterKey = JSON.stringify(filter);

  useEffect(() => {
    if (!row || !col) return;
    setError('');
    api.crosstab(surveyId, row, col, filter).then(setData, (e) => setError(e instanceof ApiError ? e.message : t('resp.load_failed')));
    // filterKey stands in for the filter object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId, row, col, filterKey]);

  if (usable.length < 2) {
    return (
      <div className="card">
        <Empty icon={<Table2 />} title={t('xtab.need_two')}>
          <p className="small">{t('xtab.need_two_hint')}</p>
        </Empty>
      </div>
    );
  }

  const value = (n: number, i: number, j: number) => {
    if (!data) return '';
    const d = mode === 'row' ? data.rowTotals[i]! : mode === 'col' ? data.colTotals[j]! : mode === 'total' ? data.total : 0;
    if (mode === 'count') return formatNumber(Math.round(n * 100) / 100);
    return d ? `${formatNumber(Math.round((n / d) * 1000) / 10)}%` : '—';
  };
  const max = data ? Math.max(1, ...data.cells.flat()) : 1;
  const chi = data?.chiSquare;

  return (
    <div className="card">
      <div className="card-head">
        <div className="tile">
          <Table2 />
        </div>
        <div className="grow">
          <h2>{t('xtab.title')}</h2>
          <div className="sub">{t('xtab.sub')}</div>
        </div>
      </div>
      <div className="card-pad stack gap-16" style={{ paddingTop: 0 }}>
        <div className="xtab-controls">
          <label className="field">
            <span className="label">{t('xtab.rows')}</span>
            <select className="select" value={row} onChange={(e) => setRow(e.target.value)}>
              {usable.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn ghost sm icon"
            aria-label={t('xtab.swap')}
            title={t('xtab.swap')}
            onClick={() => {
              setRow(col);
              setCol(row);
            }}
          >
            <ArrowLeftRight />
          </button>
          <label className="field">
            <span className="label">{t('xtab.columns')}</span>
            <select className="select" value={col} onChange={(e) => setCol(e.target.value)}>
              {usable.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
          <div className="seg" role="group" aria-label={t('xtab.show')}>
            {(['count', 'row', 'col', 'total'] as Mode[]).map((m) => (
              <button key={m} className={mode === m ? 'active' : ''} onClick={() => setMode(m)}>
                {t(`xtab.mode_${m}`)}
              </button>
            ))}
          </div>
        </div>
        {error && <div className="banner error">{error}</div>}
        {data && (
          <>
            <div className="table-wrap">
              <table className="table xtab">
                <thead>
                  <tr>
                    <th scope="col">
                      <span className="muted small">{data.row.label}</span>
                    </th>
                    {data.col.categories.map((c) => (
                      <th key={c.id} scope="col" className="num">
                        {c.label}
                      </th>
                    ))}
                    <th scope="col" className="num">
                      {t('xtab.total')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.row.categories.map((r, i) => (
                    <tr key={r.id}>
                      <th scope="row">{r.label}</th>
                      {data.cells[i]!.map((n, j) => (
                        <td key={j} className="num xtab-cell" style={{ ['--heat' as string]: String(n / max) }} title={`${r.label} × ${data.col.categories[j]!.label}: ${formatNumber(n)}`}>
                          {value(n, i, j)}
                        </td>
                      ))}
                      <td className="num strong">{mode === 'count' ? formatNumber(data.rowTotals[i]!) : value(data.rowTotals[i]!, i, -1)}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <th scope="row">{t('xtab.total')}</th>
                    {data.colTotals.map((n, j) => (
                      <td key={j} className="num strong">
                        {mode === 'count' ? formatNumber(n) : value(n, -1, j)}
                      </td>
                    ))}
                    <td className="num strong">{formatNumber(data.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="small">
              {chi ? (
                <>
                  {t('xtab.chi', { stat: formatNumber(chi.statistic), df: chi.df, p: chi.p < 0.001 ? '< 0.001' : `= ${formatNumber(chi.p)}` })}{' '}
                  <strong>{chi.p < 0.05 ? t('xtab.significant') : t('xtab.not_significant')}</strong>
                  {chi.lowExpected > 0.2 && <span className="muted"> {t('xtab.low_expected')}</span>}
                </>
              ) : (
                <span className="muted">{data.row.multi || data.col.multi ? t('xtab.no_chi_multi') : t('xtab.no_chi')}</span>
              )}
            </p>
            {(data.row.multi || data.col.multi) && <p className="hint">{t('xtab.multi_note')}</p>}
          </>
        )}
      </div>
    </div>
  );
}
